import { endOfYesterday, startOfMonth, startOfWeek, subMonths } from "date-fns";
import {
  Aperture,
  Clock,
  Film,
  Globe2,
  Inbox,
  Languages,
  MessageCircle,
  Repeat,
  Star,
  Workflow,
} from "lucide-react";
import { useListContext, useTranslate } from "ra-core";
import { ToggleFilterButton } from "@/components/admin/toggle-filter-button";
import { Button } from "@/components/ui/button";

import { FilterCategory } from "../filters/FilterCategory";
import { ResponsiveFilters } from "../misc/ResponsiveFilters";
import { useIsMobile } from "@/hooks/use-mobile";
import { ActiveFilterButton } from "../misc/ActiveFilterButton";
import {
  facetCount,
  useCohortFiltersEnabled,
  useContactEchoMetricsEnabled,
  useContactFilterFacets,
  useContactGeographyEnabled,
} from "./cohortFilters";
import {
  CONTINENT_OPTIONS,
  formatCodeFilter,
  parseCodeFilter,
  toggleCode,
} from "./geography";
import { useInboxEnabled } from "../whatsapp/inbox/useInbox";
import {
  DEFAULT_LANGUAGE,
  VENDORED_LANGUAGES,
  formatLanguage,
} from "../contact-language/languages";
import type {
  ContactAverageRatingBucket,
  ContactLifecycleStage,
  ContactUseCountBucket,
  ContactVideoCountBucket,
} from "../types";

// Every vendored language an operator can filter on (plan 2026-09-21 §4.2;
// waves A, B and C). Labels are literal ("italiano · Italian"), not i18n keys;
// ToggleFilterButton falls back to the label itself. The bot's live list is
// not consulted here: the filter is a projection query, not an edit.
const LANGUAGE_OPTIONS = VENDORED_LANGUAGES.filter(
  (language) => language.code !== DEFAULT_LANGUAGE,
);

const COHORT_PREFIX = "crm.whatsapp.broadcasts.cohort";

const LIFECYCLE_STAGES: ContactLifecycleStage[] = [
  "prospect",
  "abandoned_photos",
  "abandoned_story",
  "abandoned_director",
  "generating",
  "failed",
  "delivered_once",
  "repeat",
];

const VIDEO_BUCKETS: ContactVideoCountBucket[] = ["0", "1", "2plus"];

const USE_BUCKETS: ContactUseCountBucket[] = ["0", "1", "2to4", "5plus"];

const AVERAGE_RATING_BUCKETS: ContactAverageRatingBucket[] = [
  "champion",
  "happy",
  "mixed",
  "unhappy",
  "none",
];

const CONTINENT_FILTER_KEY = "phone_continent_code@in";

// Customer inbox: contacts whose open episode still needs a human reply.
const INBOX_NEEDS_REPLY_FILTER = { attention_state: "needs_reply" };

// Derived acquisition channels the dashboard-api exposes as an equality filter +
// facet (SNAPATTR-7). Snap is the required surface; the others ride the same
// machinery so the whole acquisition mix is filterable.
const ACQUISITION_CHANNELS = [
  "snap",
  "ctwa",
  "website",
  "link",
  "launchpad",
  "my_web",
  "organic",
] as const;

export const ContactListFilter = () => {
  const isMobile = useIsMobile();
  const translate = useTranslate();
  const cohortEnabled = useCohortFiltersEnabled();
  const metricsEnabled = useContactEchoMetricsEnabled();
  const showEchoFilters = cohortEnabled || metricsEnabled;
  const geographyEnabled = useContactGeographyEnabled();
  const inboxEnabled = useInboxEnabled();
  const { data: facetData } = useContactFilterFacets();
  const facets = facetData?.facets;

  return (
    <ResponsiveFilters
      searchInput={{
        placeholder: translate("resources.contacts.filters.search"),
      }}
    >
      <FilterCategory label={`${COHORT_PREFIX}.echo_activity`} icon={<Clock />}>
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label={`${COHORT_PREFIX}.echo_today`}
          value={{
            "last_echo_at@gte": endOfYesterday().toISOString(),
            "last_echo_at@lte": undefined,
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label={`${COHORT_PREFIX}.echo_this_week`}
          value={{
            "last_echo_at@gte": startOfWeek(new Date()).toISOString(),
            "last_echo_at@lte": undefined,
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label={`${COHORT_PREFIX}.echo_before_this_week`}
          value={{
            "last_echo_at@gte": undefined,
            "last_echo_at@lte": startOfWeek(new Date()).toISOString(),
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label={`${COHORT_PREFIX}.echo_before_this_month`}
          value={{
            "last_echo_at@gte": undefined,
            "last_echo_at@lte": startOfMonth(new Date()).toISOString(),
          }}
          size={isMobile ? "lg" : undefined}
        />
        <ToggleFilterButton
          className="w-auto md:w-full justify-between h-10 md:h-8"
          label={`${COHORT_PREFIX}.echo_before_last_month`}
          value={{
            "last_echo_at@gte": undefined,
            "last_echo_at@lte": subMonths(
              startOfMonth(new Date()),
              1,
            ).toISOString(),
          }}
          size={isMobile ? "lg" : undefined}
        />
      </FilterCategory>

      {inboxEnabled && (
        <FilterCategory
          label="crm.whatsapp.inbox.filter.category"
          icon={<Inbox />}
        >
          <ToggleFilterButton
            className="w-auto md:w-full justify-between h-10 md:h-8"
            label="crm.whatsapp.inbox.filter.needs_reply"
            value={INBOX_NEEDS_REPLY_FILTER}
            size={isMobile ? "lg" : undefined}
          />
        </FilterCategory>
      )}

      {showEchoFilters && (
        <>
          {geographyEnabled && (
            <FilterCategory
              label={`${COHORT_PREFIX}.geography`}
              icon={<Globe2 />}
            >
              <ContinentFilterButtons isMobile={isMobile} />
            </FilterCategory>
          )}

          <FilterCategory
            label={`${COHORT_PREFIX}.lifecycle`}
            icon={<Workflow />}
          >
            {LIFECYCLE_STAGES.map((stage) => (
              <ToggleFilterButton
                key={stage}
                className="w-auto md:w-full justify-between h-10 md:h-8"
                label={`${COHORT_PREFIX}.lifecycle_${stage}`}
                value={{ lifecycle_stage: stage }}
                count={facetCount(facets, "lifecycle_stage", stage)}
                size={isMobile ? "lg" : undefined}
              />
            ))}
          </FilterCategory>

          <FilterCategory label={`${COHORT_PREFIX}.uses`} icon={<Repeat />}>
            {USE_BUCKETS.map((bucket) => (
              <ToggleFilterButton
                key={bucket}
                className="w-auto md:w-full justify-between h-10 md:h-8"
                label={`${COHORT_PREFIX}.uses_${bucket}`}
                value={{ use_count_bucket: bucket }}
                count={facetCount(facets, "use_count_bucket", bucket)}
                size={isMobile ? "lg" : undefined}
              />
            ))}
          </FilterCategory>

          <FilterCategory label={`${COHORT_PREFIX}.videos`} icon={<Film />}>
            {VIDEO_BUCKETS.map((bucket) => (
              <ToggleFilterButton
                key={bucket}
                className="w-auto md:w-full justify-between h-10 md:h-8"
                label={`${COHORT_PREFIX}.videos_${bucket}`}
                value={{ video_count_bucket: bucket }}
                count={facetCount(facets, "video_count_bucket", bucket)}
                size={isMobile ? "lg" : undefined}
              />
            ))}
          </FilterCategory>

          <FilterCategory
            label={`${COHORT_PREFIX}.satisfaction`}
            icon={<Star />}
          >
            {AVERAGE_RATING_BUCKETS.map((bucket) => (
              <ToggleFilterButton
                key={bucket}
                className="w-auto md:w-full justify-between h-10 md:h-8"
                label={`${COHORT_PREFIX}.average_rating_${bucket}`}
                value={{ average_rating_bucket: bucket }}
                count={facetCount(facets, "average_rating_bucket", bucket)}
                size={isMobile ? "lg" : undefined}
              />
            ))}
          </FilterCategory>

          <FilterCategory
            label={`${COHORT_PREFIX}.reachability`}
            icon={<MessageCircle />}
          >
            <ToggleFilterButton
              className="w-auto md:w-full justify-between h-10 md:h-8"
              label={`${COHORT_PREFIX}.opted_in`}
              value={{ whatsapp_opt_in_status: "opted_in" }}
              count={facetCount(facets, "whatsapp_opt_in_status", "opted_in")}
              size={isMobile ? "lg" : undefined}
            />
          </FilterCategory>

          <FilterCategory
            label={`${COHORT_PREFIX}.acquisition`}
            icon={<Aperture />}
          >
            {ACQUISITION_CHANNELS.map((channel) => (
              <ToggleFilterButton
                key={channel}
                className="w-auto md:w-full justify-between h-10 md:h-8"
                label={`${COHORT_PREFIX}.acquisition_${channel}`}
                value={{ acquisition_channel: channel }}
                count={facetCount(facets, "acquisition_channel", channel)}
                size={isMobile ? "lg" : undefined}
              />
            ))}
          </FilterCategory>

          <FilterCategory
            label={`${COHORT_PREFIX}.language`}
            icon={<Languages />}
          >
            {LANGUAGE_OPTIONS.map((language) => (
              <ToggleFilterButton
                key={language.code}
                className="w-auto md:w-full justify-between h-10 md:h-8"
                label={formatLanguage(language)}
                value={{ preferred_language: language.code }}
                count={facetCount(facets, "preferred_language", language.code)}
                size={isMobile ? "lg" : undefined}
              />
            ))}
          </FilterCategory>
        </>
      )}
    </ResponsiveFilters>
  );
};

export const ContactListFilterSummary = () => {
  const cohortEnabled = useCohortFiltersEnabled();
  const metricsEnabled = useContactEchoMetricsEnabled();
  const showEchoFilters = cohortEnabled || metricsEnabled;
  const geographyEnabled = useContactGeographyEnabled();
  const inboxEnabled = useInboxEnabled();
  const { filterValues } = useListContext();
  const hasFilters = !!Object.entries(filterValues || {}).filter(
    ([key]) => key !== "q",
  ).length;

  if (!hasFilters) {
    return null;
  }

  return (
    <div className="flex flex-wrap items-start mb-4 gap-1">
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label={`${COHORT_PREFIX}.echo_today`}
        value={{
          "last_echo_at@gte": endOfYesterday().toISOString(),
          "last_echo_at@lte": undefined,
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label={`${COHORT_PREFIX}.echo_this_week`}
        value={{
          "last_echo_at@gte": startOfWeek(new Date()).toISOString(),
          "last_echo_at@lte": undefined,
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label={`${COHORT_PREFIX}.echo_before_this_week`}
        value={{
          "last_echo_at@gte": undefined,
          "last_echo_at@lte": startOfWeek(new Date()).toISOString(),
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label={`${COHORT_PREFIX}.echo_before_this_month`}
        value={{
          "last_echo_at@gte": undefined,
          "last_echo_at@lte": startOfMonth(new Date()).toISOString(),
        }}
      />
      <ActiveFilterButton
        className="w-auto justify-between h-8"
        label={`${COHORT_PREFIX}.echo_before_last_month`}
        value={{
          "last_echo_at@gte": undefined,
          "last_echo_at@lte": subMonths(
            startOfMonth(new Date()),
            1,
          ).toISOString(),
        }}
      />

      {inboxEnabled && (
        <ActiveFilterButton
          className="w-auto justify-between h-8"
          label="crm.whatsapp.inbox.filter.needs_reply"
          value={INBOX_NEEDS_REPLY_FILTER}
        />
      )}

      {showEchoFilters && (
        <>
          {geographyEnabled && <ActiveContinentFilterButton />}
          {LIFECYCLE_STAGES.map((stage) => (
            <ActiveFilterButton
              key={`lifecycle_${stage}`}
              className="w-auto justify-between h-8"
              label={`${COHORT_PREFIX}.lifecycle_${stage}`}
              value={{ lifecycle_stage: stage }}
            />
          ))}
          {USE_BUCKETS.map((bucket) => (
            <ActiveFilterButton
              key={`uses_${bucket}`}
              className="w-auto justify-between h-8"
              label={`${COHORT_PREFIX}.uses_${bucket}`}
              value={{ use_count_bucket: bucket }}
            />
          ))}
          {VIDEO_BUCKETS.map((bucket) => (
            <ActiveFilterButton
              key={`videos_${bucket}`}
              className="w-auto justify-between h-8"
              label={`${COHORT_PREFIX}.videos_${bucket}`}
              value={{ video_count_bucket: bucket }}
            />
          ))}
          {AVERAGE_RATING_BUCKETS.map((bucket) => (
            <ActiveFilterButton
              key={`average_rating_${bucket}`}
              className="w-auto justify-between h-8"
              label={`${COHORT_PREFIX}.average_rating_${bucket}`}
              value={{ average_rating_bucket: bucket }}
            />
          ))}
          <ActiveFilterButton
            className="w-auto justify-between h-8"
            label={`${COHORT_PREFIX}.opted_in`}
            value={{ whatsapp_opt_in_status: "opted_in" }}
          />
          {ACQUISITION_CHANNELS.map((channel) => (
            <ActiveFilterButton
              key={`acquisition_${channel}`}
              className="w-auto justify-between h-8"
              label={`${COHORT_PREFIX}.acquisition_${channel}`}
              value={{ acquisition_channel: channel }}
            />
          ))}
          {LANGUAGE_OPTIONS.map((language) => (
            <ActiveFilterButton
              key={`language_${language.code}`}
              className="w-auto justify-between h-8"
              label={formatLanguage(language)}
              value={{ preferred_language: language.code }}
            />
          ))}
        </>
      )}
    </div>
  );
};

function ContinentFilterButtons({ isMobile }: { isMobile: boolean }) {
  const { filterValues, setFilters } = useListContext();
  const translate = useTranslate();
  const selected = parseCodeFilter(filterValues?.[CONTINENT_FILTER_KEY]);

  const setSelected = (codes: string[]) => {
    const nextValue = formatCodeFilter(codes);
    const nextFilters = { ...(filterValues ?? {}) };
    if (nextValue) {
      nextFilters[CONTINENT_FILTER_KEY] = nextValue;
    } else {
      delete nextFilters[CONTINENT_FILTER_KEY];
    }
    setFilters(nextFilters);
  };

  return (
    <>
      {CONTINENT_OPTIONS.map((option) => {
        const active = selected.includes(option.code);
        return (
          <Button
            key={option.code}
            type="button"
            variant={active ? "secondary" : "ghost"}
            className="w-auto md:w-full justify-between h-10 md:h-8"
            size={isMobile ? "lg" : "sm"}
            onClick={() => setSelected(toggleCode(selected, option.code))}
          >
            {translate(option.labelKey, { _: option.fallback })}
          </Button>
        );
      })}
    </>
  );
}

function ActiveContinentFilterButton() {
  const { filterValues, setFilters } = useListContext();
  const selected = parseCodeFilter(filterValues?.[CONTINENT_FILTER_KEY]);
  const translate = useTranslate();

  if (!selected.length) return null;

  const label = selected
    .map((code) => {
      const option = CONTINENT_OPTIONS.find((item) => item.code === code);
      return translate(option?.labelKey ?? code, {
        _: option?.fallback ?? code,
      });
    })
    .join(", ");

  const clear = () => {
    const nextFilters = { ...(filterValues ?? {}) };
    delete nextFilters[CONTINENT_FILTER_KEY];
    setFilters(nextFilters);
  };

  return (
    <Button
      type="button"
      variant="secondary"
      className="w-auto justify-between h-8"
      size="sm"
      onClick={clear}
    >
      {translate(`${COHORT_PREFIX}.geography`)}: {label}
    </Button>
  );
}
